// ═══ EXECUTION CONFIRM MODAL — slide-to-execute with full fee disclosure ═══
// Extracted from SwipeCards.js (iter 96) for reuse across QuickTradeModal
// and OptionsTradingFlow. Single-leg + multi-leg variants.
//
// Single-leg: posts /order/preview → shows Gross/Fees/Slippage/Total → slide
// 80% of track → posts /order/place and calls onConfirmed(result).
//
// Multi-leg: accepts legs[] (each {symbol, side, quantity, limitPrice,
// instrumentType:'OPTION', osi, openClose}) + strategyLabel. Fans out
// /order/preview per leg in parallel, aggregates totals, and on slide-to-
// confirm sequentially posts /order/place for each leg, reporting per-leg
// status in onConfirmed([{leg, result}, ...]).

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { hapticSuccess } from "../../utils/haptics";
import { Skel } from "./Skel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────────────────────────── SINGLE-LEG ─────────────────────────────────
export function ExecutionConfirmModal({ order, onClose, onConfirmed }) {
  // order: {symbol, side: 'BUY'|'SELL', quantity, limitPrice, instrumentType,
  //         osi, openClose, orderType, amount, label}
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [slideX, setSlideX] = useState(0);
  const dragRef = useRef({ tracking: false, startX: 0, maxX: 0 });
  const trackRef = useRef(null);

  useEffect(() => {
    // zbucks fills never touch the broker — a real fee/slippage preview
    // doesn't apply, so skip the network round-trip and show a flat preview.
    if (order.fundingSource === "zbucks") {
      const mult = order.instrumentType === "OPTION" ? 100 : 1;
      const gross = (order.limitPrice || 0) * (order.quantity || 0) * mult;
      setPreview({
        quantity: order.quantity, price: order.limitPrice, gross,
        fees: 0, slippageEstimate: 0, total: gross,
        synthetic: true, syntheticReason: "zBucks paper trade — no broker fees apply",
      });
      setLoading(false);
      return;
    }
    let stop = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/order/preview`, {
          symbol: order.symbol, osi: order.osi,
          instrumentType: order.instrumentType || "EQUITY",
          side: order.side, quantity: order.quantity, limitPrice: order.limitPrice,
          openClose: order.openClose || "OPEN", amount: order.amount,
          orderType: order.orderType || "LIMIT",
        });
        if (stop) return;
        if (r.data?.ok) setPreview(r.data);
        else setError(r.data?.error || "Preview unavailable");
      } catch (e) {
        if (!stop) setError(e?.response?.data?.detail || e?.message || "Preview failed");
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [order]);

  const onPointerDown = (e) => {
    if (submitting) return;
    const rect = trackRef.current?.getBoundingClientRect();
    dragRef.current = { tracking: true, startX: e.clientX, maxX: (rect?.width || 260) - 56 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.tracking || submitting) return;
    const dx = Math.max(0, Math.min(dragRef.current.maxX, e.clientX - dragRef.current.startX));
    setSlideX(dx);
  };
  const onPointerUp = async () => {
    if (!dragRef.current.tracking || submitting) return;
    dragRef.current.tracking = false;
    if (slideX >= dragRef.current.maxX * 0.80) {
      setSubmitting(true);
      try { hapticSuccess && hapticSuccess(); } catch {}
      try {
        const r = await axios.post(`${API}/order/place`, {
          symbol: order.symbol, osi: order.osi, ticker: order.ticker,
          instrumentType: order.instrumentType || "EQUITY",
          side: order.side, quantity: order.quantity, limitPrice: order.limitPrice,
          openClose: order.openClose || "OPEN", amount: order.amount,
          orderType: order.orderType || "LIMIT",
          fundingSource: order.fundingSource || "usd",
          // Real contract details, when the caller has them — /order/place's
          // zbucks branch stores these so a paper position shows a readable
          // "AAPL $150 Call" instead of just the bare OSI. Silently dropped
          // before (this whitelist never forwarded them even when the
          // caller-built order object carried real values), so every zbucks
          // options fill stored strike/optionType/expiry as null.
          strike: order.strike, optionType: order.optionType, expiry: order.expiry,
        });
        if (r.data?.ok) {
          onConfirmed && onConfirmed(r.data);
          onClose();
        } else {
          setError(r.data?.error || "Execution failed");
          setSubmitting(false);
          setSlideX(0);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Execution failed");
        setSubmitting(false);
        setSlideX(0);
      }
    } else {
      setSlideX(0); // Cancel → snap back
    }
  };

  const sideColor = order.side === "BUY" ? "#4ade80" : "#f87171";

  return (
    <div onClick={onClose} data-testid="exec-confirm-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 20050,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          background: "var(--bg, #060a12)", border: `1px solid ${sideColor}40`,
          padding: "24px 20px 30px", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: sideColor, letterSpacing: 2, padding: "3px 8px", borderRadius: 6, background: `${sideColor}18` }}>
            {order.side}
          </span>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3" }}>{order.label || order.symbol}</div>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: "#8b949e", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {loading && <><Skel w="100%" h={70} mb={8} radius={12} /><Skel w="100%" h={40} mb={0} radius={12} /></>}

        {error && (
          <div style={{ padding: 12, borderRadius: 10, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", fontSize: 12, color: "#f87171", marginBottom: 12 }} data-testid="exec-error">
            {error}
          </div>
        )}

        {preview && !loading && (() => {
          const sym = order.fundingSource === "zbucks" ? "Ⓩ" : "$";
          return (
          <>
            <div style={{ padding: 14, borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "#8b949e", letterSpacing: 1.5 }}>QUANTITY × PRICE</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3" }}>{preview.quantity} × {sym}{preview.price}</span>
              </div>
              <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#8b949e", marginBottom: 4 }}>
                <span>Gross</span><span>{sym}{preview.gross.toLocaleString()}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#f87171", marginBottom: 4 }} data-testid="exec-fees">
                <span>Regulatory fees</span><span>{preview.fees > 0 ? `-${sym}${preview.fees.toFixed(4)}` : `${sym}0.00`}</span>
              </div>
              {preview.slippageEstimate != null && preview.slippageEstimate > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#fbbf24", marginBottom: 4 }} data-testid="exec-slippage">
                  <span>Est. slippage (~10 bps)</span>
                  <span>-{sym}{preview.slippageEstimate.toFixed(4)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, color: "#e6edf3", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)" }} data-testid="exec-total">
                <span>{order.side === "BUY" ? "Total cost" : "Net proceeds"}</span>
                <span style={{ color: sideColor }}>{sym}{preview.total.toLocaleString()}</span>
              </div>
              {preview.buyingPowerAfter != null && (
                <div style={{ fontSize: 10, color: "#636e7b", marginTop: 8 }}>
                  Buying power after: {sym}{Number(preview.buyingPowerAfter).toLocaleString()}
                </div>
              )}
              {preview.synthetic && (
                <div style={{ fontSize: 10, color: "#fbbf24", marginTop: 6, opacity: 0.9 }} data-testid="exec-synthetic-note">
                  Estimate · broker preview unavailable ({preview.syntheticReason || "market closed"})
                </div>
              )}
            </div>

            {/* Slide-to-confirm */}
            <div
              ref={trackRef}
              data-testid="exec-slide-track"
              style={{
                position: "relative", height: 56, borderRadius: 28,
                background: `${sideColor}20`, border: `1px solid ${sideColor}50`,
                overflow: "hidden", userSelect: "none", touchAction: "pan-y",
              }}
            >
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: `${sideColor}`, letterSpacing: 2, opacity: 1 - slideX / 160 }}>
                SLIDE TO {order.side === "BUY" ? "BUY" : "SELL"} →
              </div>
              <div
                data-testid="exec-slide-thumb"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => { dragRef.current.tracking = false; setSlideX(0); }}
                style={{
                  position: "absolute", top: 3, left: 3 + slideX, width: 50, height: 50,
                  borderRadius: 25, background: sideColor, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, fontWeight: 800, cursor: submitting ? "wait" : "grab",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
                  transition: dragRef.current.tracking ? "none" : "left 220ms ease",
                  touchAction: "none",
                }}
              >{submitting ? "…" : "→"}</div>
            </div>
            <div style={{ fontSize: 10, color: "#636e7b", textAlign: "center", marginTop: 10 }}>
              Live preview. Fees shown above are regulatory only.
              Execution uses a LIMIT order at the indicated price.
            </div>
          </>
          );
        })()}
      </div>
    </div>
  );
}


// ───────────────────────────── MULTI-LEG ──────────────────────────────────
// Used by OptionsTradingFlow for strategy orders (verticals, iron condor,
// butterfly, etc.). Accepts a pre-built legs[] array + a net debit/credit
// label for the strategy.
export function MultiLegExecutionConfirmModal({ legs, strategyLabel, netCost, ticker, fundingSource = "usd", onClose, onConfirmed }) {
  // legs: [{symbol, side:'BUY'|'SELL', quantity, limitPrice, instrumentType:'OPTION', osi, openClose}]
  const [previews, setPreviews] = useState([]);   // per-leg preview data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [legStatus, setLegStatus] = useState({}); // idx → "pending"|"placed"|"failed"
  const [slideX, setSlideX] = useState(0);
  const dragRef = useRef({ tracking: false, startX: 0, maxX: 0 });
  const trackRef = useRef(null);
  const isZbucks = fundingSource === "zbucks";
  const cur = isZbucks ? "Ⓩ" : "$";

  useEffect(() => {
    // zbucks fills never touch the broker — same reasoning as the single-leg
    // modal, a flat synthetic preview per leg instead of a network round-trip.
    if (isZbucks) {
      setPreviews(legs.map(leg => {
        const mult = (leg.instrumentType || "OPTION") === "OPTION" ? 100 : 1;
        const gross = (leg.limitPrice || 0) * (leg.quantity || 0) * mult;
        return { ok: true, gross, fees: 0, slippageEstimate: 0, synthetic: true };
      }));
      setLoading(false);
      return;
    }
    let stop = false;
    (async () => {
      try {
        const results = await Promise.all(
          legs.map(leg => axios.post(`${API}/order/preview`, {
            symbol: leg.symbol, osi: leg.osi,
            instrumentType: leg.instrumentType || "OPTION",
            side: leg.side, quantity: leg.quantity, limitPrice: leg.limitPrice,
            openClose: leg.openClose || "OPEN",
            orderType: leg.orderType || "LIMIT",
          }).then(r => r.data).catch(e => ({ ok: false, error: e?.message || "preview failed" })))
        );
        if (stop) return;
        setPreviews(results);
      } catch (e) {
        if (!stop) setError(e?.message || "Preview failed");
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legs, isZbucks]);

  // Aggregate totals (signed gross = debit for BUY, credit for SELL)
  const agg = previews.reduce((a, p, i) => {
    if (!p?.ok) return a;
    const leg = legs[i];
    const gross = p.gross || 0;
    const signedGross = leg.side === "BUY" ? gross : -gross;
    return {
      signedGross: a.signedGross + signedGross,
      fees: a.fees + (p.fees || 0),
      slippage: a.slippage + (p.slippageEstimate || 0),
    };
  }, { signedGross: 0, fees: 0, slippage: 0 });
  const isDebit = agg.signedGross >= 0;
  const netLabel = isDebit ? "Net debit" : "Net credit";
  const netColor = isDebit ? "#f87171" : "#4ade80";
  const netTotal = Math.abs(agg.signedGross) + agg.fees + agg.slippage;
  const sideColor = "#60a5fa"; // multi-leg uses neutral blue

  const onPointerDown = (e) => {
    if (submitting) return;
    const rect = trackRef.current?.getBoundingClientRect();
    dragRef.current = { tracking: true, startX: e.clientX, maxX: (rect?.width || 260) - 56 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.tracking || submitting) return;
    const dx = Math.max(0, Math.min(dragRef.current.maxX, e.clientX - dragRef.current.startX));
    setSlideX(dx);
  };
  const onPointerUp = async () => {
    if (!dragRef.current.tracking || submitting) return;
    dragRef.current.tracking = false;
    if (slideX < dragRef.current.maxX * 0.80) {
      setSlideX(0);
      return;
    }
    // Confirmed — execute legs sequentially so subsequent legs see the
    // previous fill (helps avoid double-sizing risk).
    setSubmitting(true);
    try { hapticSuccess && hapticSuccess(); } catch {}
    const results = [];
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i];
      setLegStatus(s => ({ ...s, [i]: "pending" }));
      try {
        const r = await axios.post(`${API}/order/place`, {
          symbol: leg.symbol, osi: leg.osi, ticker: leg.ticker,
          instrumentType: leg.instrumentType || "OPTION",
          side: leg.side, quantity: leg.quantity, limitPrice: leg.limitPrice,
          openClose: leg.openClose || "OPEN",
          orderType: leg.orderType || "LIMIT",
          fundingSource,
          strike: leg.strike, optionType: leg.optionType, expiry: leg.expiry,
        });
        if (r.data?.ok) {
          setLegStatus(s => ({ ...s, [i]: "placed" }));
          results.push({ leg, ok: true, order: r.data.order });
        } else {
          setLegStatus(s => ({ ...s, [i]: "failed" }));
          results.push({ leg, ok: false, error: r.data?.error });
        }
      } catch (e) {
        setLegStatus(s => ({ ...s, [i]: "failed" }));
        results.push({ leg, ok: false, error: e?.message });
      }
    }
    // Report combined result even if partial fills so the caller can display.
    onConfirmed && onConfirmed(results);
    // Auto-close 1s after all attempts so the user sees the final status flash.
    setTimeout(() => { onClose && onClose(); }, 1000);
  };

  const pillColor = (st) => st === "placed" ? "#4ade80" : st === "failed" ? "#f87171" : st === "pending" ? "#fbbf24" : "#636e7b";
  const pillText = (st) => st === "placed" ? "✓ PLACED" : st === "failed" ? "✕ FAILED" : st === "pending" ? "…" : "QUEUED";

  return (
    <div onClick={submitting ? undefined : onClose} data-testid="exec-confirm-modal-multileg"
      style={{
        position: "fixed", inset: 0, zIndex: 20050,
        background: "rgba(0,0,0,0.72)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          background: "var(--bg, #060a12)", border: `1px solid ${sideColor}40`,
          padding: "24px 20px 30px", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: sideColor, letterSpacing: 2, padding: "3px 8px", borderRadius: 6, background: `${sideColor}18` }}>
            {legs.length} LEG{legs.length > 1 ? "S" : ""}
          </span>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e6edf3" }}>{strategyLabel || `${ticker || ""} Strategy`}</div>
          {isZbucks && <span style={{ fontSize: 9, fontWeight: 800, color: "#fbbf24", letterSpacing: 1, padding: "3px 7px", borderRadius: 6, background: "rgba(251,191,36,0.15)" }}>ZBUCKS</span>}
          {!submitting && (
            <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: "#8b949e", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
          )}
        </div>

        {loading && <><Skel w="100%" h={90} mb={8} radius={12} /><Skel w="100%" h={40} mb={0} radius={12} /></>}

        {error && (
          <div style={{ padding: 12, borderRadius: 10, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", fontSize: 12, color: "#f87171", marginBottom: 12 }} data-testid="exec-error">
            {error}
          </div>
        )}

        {!loading && (
          <>
            {/* Per-leg breakdown */}
            <div style={{ marginBottom: 12 }}>
              {legs.map((leg, i) => {
                const p = previews[i] || {};
                const legGross = p.ok ? p.gross : 0;
                const legSide = leg.side === "BUY" ? "debit" : "credit";
                const legColor = leg.side === "BUY" ? "#f87171" : "#4ade80";
                const st = legStatus[i];
                return (
                  <div key={i} data-testid={`exec-leg-${i}`}
                    style={{
                      padding: "10px 12px", borderRadius: 10,
                      background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)",
                      marginBottom: 6, display: "flex", alignItems: "center", gap: 10,
                    }}
                  >
                    <span style={{ fontSize: 9, fontWeight: 800, color: legColor, padding: "2px 6px", borderRadius: 4, background: `${legColor}18`, letterSpacing: 1 }}>
                      {leg.side}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "#e6edf3", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {leg.quantity} × {leg.osi || leg.symbol}
                      </div>
                      <div style={{ fontSize: 10, color: "#8b949e", marginTop: 1 }}>
                        @ {cur}{Number(leg.limitPrice).toFixed(2)} · {legSide} {cur}{legGross.toFixed(2)}
                      </div>
                    </div>
                    {submitting && (
                      <span style={{ fontSize: 9, fontWeight: 800, color: pillColor(st), letterSpacing: 1, padding: "2px 6px", borderRadius: 4, background: `${pillColor(st)}22`, minWidth: 52, textAlign: "center" }}>
                        {pillText(st)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Aggregate */}
            <div style={{ padding: 14, borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 12 }}>
              {isZbucks && (
                <div style={{ fontSize: 10, color: "#fbbf24", marginBottom: 8 }}>zBucks paper trade — no broker fees or slippage apply.</div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#8b949e", marginBottom: 4 }}>
                <span>Sum of fees</span><span>{cur}{agg.fees.toFixed(4)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#fbbf24", marginBottom: 4 }}>
                <span>Est. slippage total</span><span>-{cur}{agg.slippage.toFixed(4)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, color: "#e6edf3", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.05)" }} data-testid="exec-multileg-total">
                <span>{netLabel}</span>
                <span style={{ color: netColor }}>{cur}{netTotal.toFixed(2)}</span>
              </div>
            </div>

            {/* Slide-to-confirm */}
            <div
              ref={trackRef}
              data-testid="exec-slide-track"
              style={{
                position: "relative", height: 56, borderRadius: 28,
                background: `${sideColor}20`, border: `1px solid ${sideColor}50`,
                overflow: "hidden", userSelect: "none", touchAction: "pan-y",
                opacity: submitting ? 0.6 : 1,
              }}
            >
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: sideColor, letterSpacing: 2, opacity: submitting ? 0.5 : (1 - slideX / 160) }}>
                {submitting ? "PLACING…" : `SLIDE TO CONFIRM ${legs.length} LEGS →`}
              </div>
              <div
                data-testid="exec-slide-thumb"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => { dragRef.current.tracking = false; setSlideX(0); }}
                style={{
                  position: "absolute", top: 3, left: 3 + slideX, width: 50, height: 50,
                  borderRadius: 25, background: sideColor, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, fontWeight: 800, cursor: submitting ? "wait" : "grab",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
                  transition: dragRef.current.tracking ? "none" : "left 220ms ease",
                  touchAction: "none",
                }}
              >{submitting ? "…" : "→"}</div>
            </div>
            <div style={{ fontSize: 10, color: "#636e7b", textAlign: "center", marginTop: 10 }}>
              Multi-leg orders execute each leg sequentially as LIMIT.
              If one leg fails the remaining legs still attempt to fill.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
